import React, { useState } from 'react';
import { 
  Container, 
  CardMedia,
  Typography, 
  Box, 
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  useTheme,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';

import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';

import emailjs from 'emailjs-com';

// Replace these with your actual EmailJS credentials for affiliate form
const AFFILIATE_SERVICE_ID = 'YOUR_AFFILIATE_SERVICE_ID';
const AFFILIATE_TEMPLATE_ID = 'YOUR_AFFILIATE_TEMPLATE_ID';
const PUBLIC_KEY = 'YOUR_PUBLIC_KEY';

/* const SERVICE_ID = 'service_8xd2ccb';
const TEMPLATE_ID = 'template_i346kzp';
const PUBLIC_KEY = 'Xh131ee4HN8vrNER2'; */


const AffiliatePage = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    website: '',
    experience: '',
    audience: '',
    motivation: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error' | null;
    text: string;
  }>({ type: null, text: '' });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: null, text: '' });

    try {
      const templateParams = {
        to_name: 'Affiliate Team',
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        company: formData.company,
        website: formData.website,
        experience: formData.experience,
        audience: formData.audience,
        motivation: formData.motivation,
        reply_to: formData.email,
      };

      // Using the same pattern as your working contact page
      await emailjs.send(AFFILIATE_SERVICE_ID, AFFILIATE_TEMPLATE_ID, templateParams, PUBLIC_KEY);
      
      setSubmitMessage({
        type: 'success',
        text: 'Application submitted successfully! We\'ll review your application and get back to you within 2-3 business days.'
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        website: '',
        experience: '',
        audience: '',
        motivation: ''
      });

    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Failed to submit application. Please try again or contact us directly at affiliates@recruitmentintelligence.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const commissionTiers = [
    {
      tier: 'Bronze Partner',
      referrals: '1-10 referrals/month',
      commission: '10%',
      bonuses: ['Marketing materials', 'Monthly webinars'],
      color: '#CD7F32'
    },
    {
      tier: 'Silver Partner',
      referrals: '11-25 referrals/month',
      commission: '15%',
      bonuses: ['Marketing materials','Monthly webinars','Priority support'],
      color: '#C0C0C0'
    },
    {
      tier: 'Gold Partner',
      referrals: '25+ referrals/month',
      commission: '20%',
      bonuses: ['Marketing materials','Monthly webinars','Priority support','Dedicated account manager'],
      color: '#FFD700'
    },
   /*  {
      tier: 'Platinum Partner',
      referrals: '31+ referrals/month',
      commission: '30%',
      bonuses: ['Revenue sharing', 'Product roadmap input', 'Executive access'],
      color: '#E5E4E2'
    } */
  ];

  const benefits = [
    {
      icon: <MonetizationOnIcon />,
      title: 'Competitive Commissions',
      description: 'Earn 10-20% recurring commissions on all successful referrals with no cap on earnings (up to $300 per sale)'
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Growing Market',
      description: 'AI recruitment is a $3B+ market growing 25% annually - perfect timing to join'
    },
    {
      icon: <SupportAgentIcon />,
      title: 'Dedicated Support',
      description: 'Personal affiliate manager, marketing materials, and 24/7 support team'
    },
    {
      icon: <AnalyticsIcon />,
      title: 'Real-Time Analytics',
      description: 'Track clicks, conversions, and commissions with our advanced dashboard'
    },
    {
      icon: <BusinessCenterIcon />,
      title: 'Professional Tools',
      description: 'Custom landing pages, email templates, and sales collateral'
    },
    {
      icon: <HandshakeIcon />,
      title: 'Long-Term Partnership',
      description: 'Build lasting relationships with recurring revenue and growth opportunities'
    }
  ];

  const requirements = [
    'Established audience in HR, recruiting, or business sectors',
    'Professional website or strong social media presence',
    'Commitment to ethical marketing practices',
    'Ability to generate quality leads consistently',
    'Understanding of recruitment industry challenges (a plus)'
  ];

  const steps = [
    {
      step: 1,
      title: 'Apply',
      description: 'Submit your application with details about your audience and experience'
    },
    {
      step: 2,
      title: 'Review',
      description: 'Our team reviews your application within 2-3 business days'
    },
    {
      step: 3,
      title: 'Onboard',
      description: 'Get access to your dashboard, materials, and dedicated support'
    },
    {
      step: 4,
      title: 'Promote',
      description: 'Start promoting and earning commissions on successful referrals'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.primary.main}10)`,
          border: `1px solid ${theme.palette.primary.light}30`
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: 1.5,
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            AFFILIATE PROGRAM
          </Typography>
        </Box>

        <Typography variant="h5" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          textAlign: 'center',
          fontSize: { xs: '1.5rem', md: '1.75rem' }
        }}>
          Partner with RECRUITMENT INTELLIGENCE®
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ 
          textAlign: 'center', 
          maxWidth: '900px', 
          mx: 'auto',
          fontSize: '1.05rem',
          lineHeight: 1.6
        }}>
          Join our exclusive affiliate program and earn substantial commissions by promoting the world's most advanced AI-powered recruitment platform. Built by Forbes-ranked recruiters with 40+ years of expertise.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
          <Chip 
            label="Up to $300 per Sale" 
            color="primary" 
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
          <Chip 
            label="Recurring Revenue" 
            color="secondary" 
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
          <Chip 
            label="No Earning Limits" 
            color="success" 
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Paper>

      {/* Commission Structure */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StarIcon sx={{ color: 'primary.main', mr: 1, fontSize: 30 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Commission Structure & Tiers
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="body1" gutterBottom sx={{ mb: 3, fontSize: '1.05rem' }}>
          Our tiered commission structure rewards performance and growth. The more you refer, the more you earn:
        </Typography>

        <Grid container spacing={3}>
          {commissionTiers.map((tier, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card 
                elevation={2} 
                sx={{ 
                  height: '100%',
                  borderTop: `4px solid ${tier.color}`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: tier.color }}>
                    {tier.tier}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {tier.referrals}
                  </Typography>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700, mb: 2 }}>
                    {tier.commission}
                  </Typography>
                  <Typography variant="caption" sx={{ mb: 2, display: 'block' }}>
                    Commission Rate
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ textAlign: 'left' }}>
                    {tier.bonuses.map((bonus, bonusIndex) => (
                      <Box key={bonusIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                        <Typography variant="caption">
                          {bonus}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

           {/* Centered Image */}
           <Box sx={{ mb: 4, maxWidth: '900px', mx: 'auto' }}>
          <Paper 
              elevation={3} 
              sx={{ 
                overflow: 'hidden',
                borderRadius: 0,
                width: 'fit-content',
                mx: 'auto'
              }}
            >
              <CardMedia
                component="img"
                // image="/assets/AdobeStock_128876597.jpeg?auto=compress"
                image="/assets/Affiliate1.png?auto=compress"                
                alt="Team collaborating on recruitment strategy"
                sx={{ 
                  width: '100%',
                  height: 'auto',
                  // maxHeight: { xs: '300px', md: '400px' },
                  objectFit: 'cover',
                  mx: 'auto'
                }}
              />
            </Paper>
          </Box>

      {/* Benefits Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <GroupIcon sx={{ color: 'primary.main', mr: 1, fontSize: 30 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Why Join Our Affiliate Program?
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={1} sx={{ 
                height: '100%', 
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ 
                      mr: 2, 
                      mt: 0.5,
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: `${theme.palette.primary.main}15`
                    }}>
                      {React.cloneElement(benefit.icon, { 
                        sx: { color: 'primary.main', fontSize: 24 } 
                      })}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Requirements & Process */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* Requirements */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Partner Requirements
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {requirements.map((requirement, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={requirement}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Process */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              How It Works
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {steps.map((step, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    fontWeight: 600
                  }}>
                    {step.step}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                  {step.description}
                </Typography>
                {index < steps.length - 1 && (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={100} 
                      sx={{ 
                        width: 2, 
                        height: 20,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.primary.light
                        }
                      }} 
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Application Form */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Apply to Become an Affiliate Partner
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Applications are reviewed within 2-3 business days. We'll contact you with next steps.
        </Alert>

        {submitMessage.type && (
          <Alert severity={submitMessage.type} sx={{ mb: 3 }}>
            {submitMessage.text}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                required
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                required
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company}
                onChange={handleInputChange('company')}
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: <BusinessCenterIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website URL"
                value={formData.website}
                onChange={handleInputChange('website')}
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: <LanguageIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Affiliate Marketing Experience</InputLabel>
                <Select
                  value={formData.experience}
                  onChange={handleInputChange('experience')}
                  label="Affiliate Marketing Experience"
                  disabled={isSubmitting}
                >
                  <MenuItem value="beginner">Beginner (0-1 years)</MenuItem>
                  <MenuItem value="intermediate">Intermediate (2-5 years)</MenuItem>
                  <MenuItem value="advanced">Advanced (5+ years)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describe Your Audience"
                value={formData.audience}
                onChange={handleInputChange('audience')}
                disabled={isSubmitting}
                placeholder="Tell us about your audience size, demographics, and how they relate to HR/recruiting..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Why do you want to become an affiliate?"
                value={formData.motivation}
                onChange={handleInputChange('motivation')}
                disabled={isSubmitting}
                placeholder="Share your motivation and how you plan to promote our platform..."
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* CTA Section */}
      <Box 
        sx={{ 
          p: 4, 
          mt: 4, 
          textAlign: 'center',
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          borderRadius: 2
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Ready to Start Earning?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}>
          Join hundreds of successful affiliates who are already earning substantial commissions by promoting the future of recruitment technology.
        </Typography>
        <Button
          variant="outlined"
          size="large"
          sx={{ 
            color: 'white',
            borderColor: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderColor: 'white'
            }
          }}
          href="mailto:affiliates@recruitmentintelligence.com"
        >
          Contact Our Affiliate Team
        </Button>
      </Box>
    </Container>
  );
};

export default AffiliatePage;
